/**
 * Utility helper functions for the Spinner Wheel
 */

import createWebShareEngine from '@firstform/json-url/web-share';
import type { WheelEntry } from '../types/wheel';

// Threshold (in URI-encoded characters) below which we skip compression
// and use the human-readable pipe-separated format instead
const PLAIN_TEXT_THRESHOLD = 2000;

// Create the json-url engine once at module level
const engine = createWebShareEngine();

/**
 * Generate a random alphanumeric ID
 */
export function makeId(length: number = 5): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

/**
 * Get a URL query parameter value
 */
export function getParamValue(param: string, url?: string): string | null {
  const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  const escapedParam = param.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${escapedParam}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(targetUrl);

  if (!results) return null;
  if (!results[2]) return '';

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle a function
 */
export function throttle<T extends (...args: Parameters<T>) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return function executedFunction(...args: Parameters<T>): void {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Parse entries from a pipe-separated string
 */
export function parseEntriesFromString(entriesString: string): WheelEntry[] {
  if (!entriesString) return [];

  return entriesString.split('|').map((name) => ({
    id: makeId(),
    name: name.trim(),
  }));
}

/**
 * Parse comma-separated input into entries
 */
export function parseCommaInput(input: string): WheelEntry[] {
  if (!input) return [];

  return input
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .map((name) => ({
      id: makeId(),
      name,
    }));
}

/**
 * Serialize entries to a URL-safe string (legacy pipe format)
 */
export function serializeEntriesToUrl(entries: WheelEntry[]): string {
  if (!entries || entries.length === 0) return '';
  return entries.map((e) => e.name).join('|');
}

/**
 * Compress entry names using json-url for compact URL sharing
 * Returns null if data is below the plain text threshold
 */
export async function compressEntries(entries: WheelEntry[]): Promise<string | null> {
  if (!entries || entries.length === 0) return '';

  try {
    const names = entries.map((e) => e.name);

    // Skip compression when the data is small enough for a readable URL
    const rawencoded = encodeURIComponent(JSON.stringify(names)).length;
    if (rawencoded <= PLAIN_TEXT_THRESHOLD) {
      return null;
    }

    return await engine.compress(names);
  } catch (e) {
    console.error('Failed to compress entries:', e);
    return null;
  }
}

/**
 * Decompress a json-url token back to entry objects
 */
export async function decompressEntries(compressed: string): Promise<WheelEntry[]> {
  if (!compressed) return [];

  try {
    const names = await engine.tryDecompress(compressed, []);
    if (!Array.isArray(names)) return [];

    return (names as string[]).map((name) => ({
      id: makeId(),
      name,
    }));
  } catch (e) {
    console.error('Failed to decompress entries:', e);
    return [];
  }
}

/**
 * Update URL with current entries without page reload
 */
export async function updateUrlWithEntries(entries: WheelEntry[]): Promise<void> {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  // Clear both old and new format params
  url.searchParams.delete('entries');
  url.searchParams.delete('s');

  if (entries && entries.length > 0) {
    const legacyString = serializeEntriesToUrl(entries);
    const compressed = await compressEntries(entries);

    // compressConditional returns null when data is below plainTextThreshold
    if (compressed && compressed.length < legacyString.length) {
      url.searchParams.set('s', compressed);
    } else {
      url.searchParams.set('entries', legacyString);
    }
  }

  // Use replaceState to avoid polluting browser history
  window.history.replaceState({}, '', url.toString());
}

/**
 * Get a shareable URL with current entries
 */
export async function getShareableUrl(entries: WheelEntry[]): Promise<string> {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.origin + window.location.pathname);

  if (entries && entries.length > 0) {
    const legacyString = serializeEntriesToUrl(entries);
    const compressed = await compressEntries(entries);

    if (compressed && compressed.length < legacyString.length) {
      url.searchParams.set('s', compressed);
    } else {
      url.searchParams.set('entries', legacyString);
    }
  }

  return url.toString();
}

/**
 * Parse entries from URL - supports both json-url compressed and pipe-separated formats
 */
export async function parseEntriesFromUrl(url?: string): Promise<WheelEntry[]> {
  // Check for compressed format first
  const compressed = getParamValue('s', url);
  if (compressed) {
    return decompressEntries(compressed);
  }

  // Fall back to legacy pipe-separated format
  const entriesParam = getParamValue('entries', url);
  if (entriesParam) {
    return parseEntriesFromString(entriesParam);
  }

  return [];
}
